import type { InlineError } from '@/lib/errorReport';

// Quote types
export interface Quote {
  id: string;
  title: string;
  slug: string;
  quoteNumber?: string;
  dealId?: string;
  dealName?: string | null;
  portalId: string;
  pdfUrl?: string;
  /** Intro-video: eigen upload (/uploads/...) of embed-URL (YouTube, Vimeo, Vidyard) */
  introVideoUrl?: string | null;
  status: QuoteStatus;
  companyName?: string;
  /** Naam van de HubSpot-gebruiker die de offerte heeft aangemaakt. */
  createdBy?: string | null;
  /** HubSpot deal-eigenaar (alleen gezet op lijst-API) */
  dealOwnerId?: string | null;
  dealOwnerName?: string | null;
  /** Alleen lijst-API: link naar medetekenen door UBO wanneer van toepassing */
  countersignUrl?: string | null;
  /** Alleen lijst-API: concept dat na aanmaken nooit is geopend of bewerkt ('spookofferte'). */
  isGhost?: boolean;
  /** Gearchiveerd in plaats van verwijderd: staat onder 'Gearchiveerd' en is terug te zetten. */
  archivedAt?: Date | null;
  /** Gezet als dit de werkkopie van een template is: de editor draait dan in templatemodus. */
  templateId?: string | null;
  /** Alleen lijst-API: afgeleide handtekeningstatus voor klant/UBO. */
  signatureSummary?: {
    /** Alleen gevuld zodra álle klant-tekenbevoegden hebben getekend. */
    recipientSignedAt: string | null;
    senderSignedAt: string | null;
    countersignRequired: boolean;
    countersignTokenAvailable: boolean;
    countersignTokenExpired: boolean;
    senderSignerId: string | null;
    senderSignerName: string | null;
    recipientsTotal?: number;
    recipientsSigned?: number;
    recipientNames?: string[];
    /** Wie er aan klantzijde moest tekenen en wie dat al gedaan heeft. */
    recipientSigners?: { name: string; signed: boolean; signedAt: string | null }[];
  };
  /** Alleen lijst-API: som van de productregels in centen (0 = nog geen investering ingevuld). */
  valueCents?: number;
  /** Alleen lijst-API: bovenkant bij van–tot prijzen; ontbreekt bij één bedrag. */
  valueMaxCents?: number;
  /** Alleen lijst-API: aantal regels dat op een oudere productversie staat. */
  outdatedProductRows?: number;
  /** Aantal keer dat de publieke offerte is geopend. */
  viewCount?: number;
  expiresAt?: string | Date;
  createdAt: Date;
  updatedAt: Date;
  blocks: Block[];
  /** o.a. presetId (UBX), pagina-instellingen, clientLogoWhiteFilterOnDark (wit klantlogo op donkere header) */
  layoutConfig?: Record<string, unknown>;
  metadata?: Record<string, any>;
  /** Alleen publieke GET /public/quotes/:slug — map voor {{tokens}} (zelfde als server-side resolve). */
  variableMap?: Record<string, string>;
  /** Taal van de gepubliceerde offerte-UI (knoppen, dialogs). */
  publicLocale?: 'nl' | 'en';
}

export enum QuoteStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  VIEWED = 'VIEWED',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  EXPIRED = 'EXPIRED',
  WITHDRAWN = 'WITHDRAWN',
}

// Block types
export interface Block {
  id: string;
  quoteId: string;
  type: BlockType;
  order: number;
  content: BlockContent;
  styles?: BlockStyles;
  config?: BlockConfig;
  createdAt: Date;
  updatedAt: Date;
}

export enum BlockType {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  TABLE = 'TABLE',
  PRODUCT = 'PRODUCT',
  PRICING = 'PRICING',
  DIVIDER = 'DIVIDER',
  SPACER = 'SPACER',
  SIGNATURE = 'SIGNATURE',
  HEADER = 'HEADER',
  // UBO Theme blocks
  COVER = 'COVER',
  TEAM = 'TEAM',
  CONTENT_PAGE = 'CONTENT_PAGE',
  INVESTMENT = 'INVESTMENT',
  PLAN_VAN_AANPAK = 'PLAN_VAN_AANPAK',
  FOOTER = 'FOOTER',
  SIGNATURE_PAGE = 'SIGNATURE_PAGE',
}

export type BlockContent =
  | TextBlockContent
  | ImageBlockContent
  | VideoBlockContent
  | TableBlockContent
  | ProductBlockContent
  | PricingBlockContent
  | DividerBlockContent
  | SpacerBlockContent
  | SignatureBlockContent
  | HeaderBlockContent
  // UBO Theme content types
  | CoverBlockContent
  | TeamBlockContent
  | ContentPageBlockContent
  | InvestmentBlockContent
  | PlanVanAanpakBlockContent
  | FooterBlockContent
  | SignaturePageBlockContent;

export interface TextBlockContent {
  html: string;
  alignment?: 'left' | 'center' | 'right' | 'justify';
}

export interface ImageBlockContent {
  url: string;
  alt?: string;
  width?: number;
  height?: number;
  alignment?: 'left' | 'center' | 'right';
}

export interface VideoBlockContent {
  url: string;
  title?: string;
  posterUrl?: string;
}

export interface TableBlockContent {
  headers: string[];
  rows: string[][];
  hasHeader?: boolean;
}

export interface ProductBlockContent {
  productId: string;
  name: string;
  description?: string;
  quantity: number;
  price: number;
  discount?: number;
  total: number;
}

export interface PricingBlockContent {
  lineItems: PricingLineItem[];
  subtotal: number;
  tax?: number;
  taxRate?: number;
  discount?: number;
  total: number;
  currency: string;
}

export interface PricingLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface DividerBlockContent {
  style?: 'solid' | 'dashed' | 'dotted';
  color?: string;
  thickness?: number;
}

export interface SpacerBlockContent {
  height: number;
}

export interface SignatureBlockContent {
  label?: string;
  signatureData?: string;
  signerName?: string;
  signedAt?: string;
  required?: boolean;
}

export interface HeaderBlockContent {
  logoUrl?: string;
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  showLogo?: boolean;
  alignment?: 'left' | 'center' | 'right';
}

// UBO Theme content types
export interface CoverBlockContent {
  title?: string;
  subtitle?: string;
  theme?: 'ubo' | 'ubx';
  logoUrl?: string;
  clientLogoUrl?: string;
  agencyLogoUrl?: string;
  backgroundUrl?: string;
}

export interface TeamBlockContent {
  title?: string;
  theme?: 'ubo' | 'ubx';
  selectedMembers?: string[];
  members?: TeamMember[];
  roleOverrides?: Record<string, string>;
  clientLogoUrl?: string;
  agencyLogoUrl?: string;
}

export interface ContentPageBlockContent {
  title?: string;
  content?: string;
  style?: 'default' | 'juridisch' | 'primary' | 'merkidentiteit' | 'ontwikkelfase' | 'positionering' | 'strategie';
  /** Alleen UBX-template: logo’s op wit zonder filter, op kleur wit; groter formaat in PageHeader. */
  theme?: 'ubo' | 'ubx';
  clientLogoUrl?: string;
  agencyLogoUrl?: string;
}

/** Valuta voor alle bedragen in het investeringsblok (editor + PDF). */
export type InvestmentCurrency = 'EUR' | 'GBP' | 'USD';

/** Taal voor vaste tabellabels (koppen, subtotaal, totaalregel). */
export type InvestmentLabelLanguage = 'nl' | 'en';

/** Optionele tarieven/kosten onder het investeringsblok (zelfde blok). */
export interface InvestmentRateCardRow {
  id: string;
  label: string;
  rateText: string;
}

export interface InvestmentRateCard {
  title?: string;
  rows: InvestmentRateCardRow[];
}

export interface InvestmentBlockContent {
  mainTitle?: string;
  theme?: 'ubo' | 'ubx';
  /** Standaard EUR. */
  currency?: InvestmentCurrency;
  /** Standaard nl. Stuurt kolomkoppen + totaalregeltekst (NL/EN presets). */
  labelLanguage?: InvestmentLabelLanguage;
  tables?: InvestmentTable[];
  /**
   * Legacy: stond de instelling ooit voor het hele blok. Wordt niet meer
   * geschreven; dient alleen nog als startwaarde voor tabellen die zelf
   * niets zeggen. Zie InvestmentTable.showHourColumns.
   */
  showHourColumns?: boolean;
  /** Optionele tabel met benoemde tarieven (onder de fasetabellen). */
  rateCard?: InvestmentRateCard;
  tableLabels?: {
    description?: string;
    /** Kolomkop uurtarief (hourly); anders valt preview terug op priceExTax. */
    hourlyRate?: string;
    priceExTax?: string;
    amountHours?: string;
    subtotal?: string;
    exTax?: string;
    /** Volledige tekst totaalregel (editor/preview); optioneel i.c.m. labelLanguage. */
    totalRow?: string;
  };
  clientLogoUrl?: string;
  agencyLogoUrl?: string;
}

export interface InvestmentTable {
  id: string;
  title: string;
  /** Secundaire regel onder de fasetitel (b.v. “per maand”). */
  subtitle?: string;
  pricingType: 'hourly' | 'fixed' | 'recurring';
  rows: InvestmentRow[];
  fixedTotal?: number;
  /** Vrije tekst in plaats van het vaste totaalbedrag, bijvoorbeeld “Op maat”. */
  fixedTotalText?: string;
  /** Totaalregel onder de tabel; weglaten of true = tonen. */
  showTotalRow?: boolean;
  /**
   * Uurtarief- en urenkolom op de offerte; weglaten of true = tonen.
   * Uit bij value based pricing: de klant ziet alleen omschrijving + bedrag,
   * terwijl uren en tarief bewaard blijven voor interne planning.
   */
  showHourColumns?: boolean;
  /**
   * Bedragen per regel op de offerte; weglaten of true = tonen. Uit: de klant ziet
   * alleen de omschrijvingen en het totaal, terwijl de regelbedragen het totaal blijven vormen.
   */
  showRowPrices?: boolean;
  /**
   * Opgeslagen documentbreedtes in CSS-pixels. De sleutels zijn semantisch,
   * zodat verborgen interne kolommen geen ruimte innemen in de klantweergave
   * en een latere wijziging van het prijsmodel geen posities door elkaar haalt.
   */
  columnWidths?: Partial<Record<'description' | 'hourlyRate' | 'hours' | 'subtotal', number>>;
}

/** Koppeling van een offerteregel aan een product uit de catalogus. */
export interface InvestmentRowProductLink {
  productId: string;
  /** Versie waarmee deze regel is toegevoegd; blijft staan als het product wijzigt. */
  productVersionId: string;
  variantId?: string;
  /** Aantal bij een prijs per eenheid (per record, per gebruiker, per profiel). */
  quantity?: number;
  /** Korting op deze regel in procenten. */
  discountPct?: number;
  /** True zodra sales een berekend veld handmatig invulde; die regel volgt de formule niet meer. */
  hasOverrides?: boolean;
}

export interface InvestmentRow extends Partial<InvestmentRowProductLink> {
  /** Named catalog rate, or custom. The price remains a quote snapshot. */
  rateKey?: string;
  /** Customer-facing hours/rate visibility; missing values inherit the phase default. */
  showHours?: boolean;
  investmentProduct?: import('../../../backend/src/utils/productInvestment').InvestmentProduct;
  productPrice?: import('../../../backend/src/utils/productInvestment').PublicProductPrice;
  id: string;
  title: string;
  /** Opgeslagen HTML voor omschrijvingscel (vet/cursief/lijsten); alleen gezet bij rich markup. Plain blijft in `title`. */
  titleHtml?: string;
  price?: number;
  hours?: number;
  subtotal?: number;
  text?: string;
  useText?: boolean;
  /** Handmatige tekst in alleen de subtotaalcel, bijvoorbeeld "Op maat". */
  subtotalText?: string;
  optional?: boolean;
  selected?: boolean;
}

export interface PlanVanAanpakBlockContent {
  title?: string;
  showCompanyData?: boolean;
  companyName?: string;
  companyAddress?: string;
  companyCity?: string;
  companyZip?: string;
  content?: string;
  theme?: 'ubo' | 'ubx';
  clientLogoUrl?: string;
  agencyLogoUrl?: string;
}

export interface FooterBlockContent {
  headline?: string;
  theme?: 'ubo' | 'ubx';
  logoUrl?: string;
  backgroundUrl?: string;
}

export interface SignatureRecipientAudit {
  documentHash?: string | null;
  signerIp?: string | null;
  signerUserAgent?: string | null;
  timestamp?: string | null;
}

/** Eén tekenbevoegde aan klantzijde; een offerte kan er meerdere hebben. */
export interface SignatureRecipient {
  id: string;
  name: string;
  email: string;
  signature?: string | null;
  signedAt?: string | null;
  audit?: SignatureRecipientAudit | null;
}

export interface SignaturePageBlockContent {
  preText?: string;
  theme?: 'ubo' | 'ubx';
  showSignature?: boolean;
  signatureData?: string;
  signerName?: string;
  signedAt?: string;
  terms?: string;
  closingText?: string;
  senderSignature?: string;
  senderName?: string;
  senderSignerId?: string;
  senderSignedAt?: string;
  /** Leidend voor de klantzijde; de `recipient*`-velden zijn hiervan de samenvatting. */
  recipients?: SignatureRecipient[];
  recipientSignature?: string;
  recipientName?: string;
  recipientEmail?: string;
  /** Alleen gevuld zodra álle tekenbevoegden hebben getekend. */
  recipientSignedAt?: string;
  clientLogoUrl?: string;
  agencyLogoUrl?: string;
}

export interface BlockStyles {
  backgroundColor?: string;
  padding?: string;
  margin?: string;
  borderRadius?: string;
  border?: string;
  textColor?: string;
  fontSize?: string;
  fontWeight?: string;
}

export interface BlockLayout {
  pageIndex?: number;
  height?: number;
  width?: number;
  offsetY?: number;
}

export interface BlockConfig {
  layout?: BlockLayout;
}

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  photoUrl?: string;
}

/** Lid van de teamlijst waaruit een teampagina kiest (GET /public/team-members). */
export interface TeamCatalogMember {
  id: string;
  name: string;
  title: string;
  photoUrl: string;
  /** Uit de teamlijst gehaald: niet meer te kiezen, wel zichtbaar op pagina's waar het al op staat. */
  archived: boolean;
}

// Template types
/** quote: een complete offerte om mee te beginnen; page: een pagina uit de paginabibliotheek. */
export type TemplateKind = 'quote' | 'page';

export interface Template {
  id: string;
  name: string;
  description?: string;
  thumbnail?: string;
  kind: TemplateKind;
  category?: string;
  isPublic: boolean;
  isMaster?: boolean;
  archivedAt?: Date;
  portalId: string;
  layoutConfig?: Record<string, any>;
  blocks: Omit<Block, 'id' | 'quoteId' | 'createdAt' | 'updatedAt'>[];
  /** Openstaande bewerking in de editor met nog niet opgeslagen wijzigingen. */
  draftQuoteId?: string;
  /** Naam van de HubSpot-gebruiker die de template heeft gemaakt; leeg bij oudere templates. */
  createdBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

// HubSpot types
/** Een deal zoals de dealkiezer bij 'Nieuwe offerte' hem toont. */
export interface DealOption {
  id: string;
  name: string;
  ownerName?: string | null;
  companyName?: string | null;
  stageLabel?: string | null;
  amount?: number | null;
  currency?: string | null;
  lastModifiedAt?: string | null;
}

export interface HubSpotDeal {
  id: string;
  pipelineInfo?: {
    id: string;
    label: string;
    stage: { id: string; label: string } | null;
  } | null;
  properties: {
    dealname: string;
    amount?: string;
    dealstage?: string;
    closedate?: string;
    [key: string]: any;
  };
}

export interface HubSpotContact {
  id: string;
  properties: {
    firstname?: string;
    lastname?: string;
    email?: string;
    phone?: string;
    [key: string]: any;
  };
}

export interface HubSpotCompany {
  id: string;
  properties: {
    name?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    domain?: string;
    industry?: string;
    phone?: string;
    [key: string]: any;
  };
}

export interface HubSpotVariable {
  tag: string;
  label: string;
  value: string;
}

// Store types
export interface QuoteStore {
  quotes: Quote[];
  currentQuote: Quote | null;
  /** Lijst/overzicht (fetchQuotes). */
  quotesLoading: boolean;
  quotesError: string | null;
  /** Enkele offerte (fetchQuote, createQuote). */
  quoteLoading: boolean;
  quoteError: InlineError | null;
  fetchQuotes: (params?: {
    portalId?: string;
    dealId?: string;
    limit?: number;
    offset?: number;
    includeArchived?: boolean;
  }) => Promise<void>;
  fetchQuote: (id: string) => Promise<void>;
  createQuote: (data: Partial<Quote>) => Promise<Quote>;
  updateQuote: (id: string, data: Partial<Quote>) => Promise<void>;
  /** Archiveren of terugzetten; de lijst past zich direct aan, zonder opnieuw te laden. */
  setQuotesArchived: (ids: string[], archived: boolean) => Promise<void>;
  setCurrentQuote: (quote: Quote | null) => void;
}

export interface BlockStore {
  blocks: Block[];
  /** Intern: snapshots vóór structurele wijzigingen (add/delete/reorder) voor undo/redo. */
  blockHistoryPast: Block[][];
  blockHistoryFuture: Block[][];
  /** Aantal block-persists (debounced of in-flight) dat nog niet door de server is bevestigd. */
  pendingBlockSaves: number;
  /** Block-id's waarvan de laatste persist-poging is mislukt (leeg = alles opgeslagen). */
  failedBlockSaves: string[];
  /** Fout van de laatst mislukte persist; leeg zodra alles weer is opgeslagen. */
  lastBlockSaveError: unknown;
  /** Monotone teller; verhoogd bij elke blokwijziging die NIET uit de TipTap-editor komt (autofill, AI), zodat de editor het doc kan hersyncen. */
  externalBlocksRevision: number;
  /** Standaard wist structurele undo/redo-history (bij laden andere offerte). Zet `preserveStructuralHistory` om history te behouden (TipTap-sync). */
  setBlocks: (blocks: Block[], options?: { preserveStructuralHistory?: boolean }) => void;
  addBlock: (block: Omit<Block, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Block>;
  /** Zet `external` als de wijziging niet uit de TipTap-editor zelf komt; dan synct de editor het doc opnieuw. */
  updateBlock: (id: string, data: Partial<Block>, options?: { external?: boolean }) => Promise<void>;
  /** Wacht tot alle debounced block-updates naar de API zijn weggeschreven (bij verlaten pagina). Geeft `false` terug als minstens één persist faalde. */
  flushPendingBlockUpdates: () => Promise<boolean>;
  deleteBlock: (id: string) => Promise<void>;
  reorderBlocks: (quoteId: string, blockIds: string[]) => Promise<void>;
  /** Structurele undo (pagina toevoegen/verwijderen/volgorde) wanneer TipTap geen history meer heeft. */
  undoBlocks: () => Promise<void>;
  redoBlocks: () => Promise<void>;
  /** Push een snapshot van de huidige blocks op de undo-stack (voor externe code die blocks wijzigt buiten addBlock/deleteBlock). */
  pushStructuralSnapshot: () => void;
}

export interface TemplateStore {
  templates: Template[];
  loading: boolean;
  error: string | null;
  fetchTemplates: (params?: {
    portalId?: string;
    includePublic?: boolean;
    includeArchived?: boolean;
    category?: string;
  }) => Promise<void>;
  createTemplate: (data: Partial<Template>) => Promise<Template>;
  applyTemplate: (templateId: string, quoteId: string) => Promise<void>;
}
